import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiBookOpen, FiExternalLink, FiLayers, FiAlertCircle, FiList, FiCopy, FiFileText, FiLayout, FiGrid, FiAlignLeft } = FiIcons;

const PublicRecommendations = () => {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('visual'); // 'visual' | 'markdown'
  const [contentLevel, setContentLevel] = useState('short'); // 'short' | 'detailed'
  const [copyFeedback, setCopyFeedback] = useState(false);

  useEffect(() => {
    const fetchRecommendations = async () => {
      try {
        setLoading(true);
        const response = await apiService.getRecommendations();
        
        if (response.data) {
          const activeRecs = response.data.filter(rec => rec.is_active === 1);
          setRecommendations(activeRecs);
        } else {
          setRecommendations([]);
        }
      } catch (err) {
        console.error('Error fetching recommendations:', err);
        setError('Konnte Empfehlungen nicht laden.');
      } finally {
        setLoading(false);
      }
    };

    fetchRecommendations();
  }, []);

  const generateMarkdown = () => {
    let md = `# Handlungsempfehlungen & Best Practices\n`;
    md += `Typ: ${contentLevel === 'short' ? 'Kurzübersicht' : 'Vollständige Details'}\n`;
    md += `Generiert am: ${new Date().toLocaleDateString()}\n\n`;

    recommendations.forEach(rec => {
      md += `## ${rec.title}\n`;
      if (rec.short_description) {
        md += `**Kurzbeschreibung**: ${rec.short_description}\n\n`;
      }
      
      if (contentLevel === 'detailed') {
        const cleanDescription = rec.description ? rec.description.replace(/<[^>]+>/g, '').trim() : '';
        md += `${cleanDescription}\n\n`;
      }
      md += `---\n\n`;
    });

    return md;
  };

  const handleCopy = () => {
    const md = generateMarkdown();
    navigator.clipboard.writeText(md).then(() => {
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mb-4"></div>
        <p className="text-gray-600">Lade Handlungsempfehlungen...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center border-l-4 border-red-500">
          <SafeIcon icon={FiAlertCircle} className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-800 mb-2">Fehler</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // Generate JSON-LD for SEO/LLMs
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Handlungsempfehlungen & Best Practices",
    "description": "Eine Sammlung von bewährten Handlungsempfehlungen und Strategien zur Team- und Organisationsentwicklung.",
    "hasPart": recommendations.map(rec => ({
      "@type": "Article",
      "headline": rec.title,
      "description": rec.short_description,
      "articleBody": contentLevel === 'detailed' ? rec.description?.replace(/<[^>]+>/g, '') : undefined
    }))
  };

  return (
    <div className="min-h-screen bg-white font-sans text-gray-800">
      {/* JSON-LD Script for LLMs/Crawlers */}
      <script type="application/ld+json">
        {JSON.stringify(jsonLd)}
      </script>

      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-[80%] w-full mx-auto px-4 py-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-brand-primary/10 p-2 rounded-lg">
              <SafeIcon icon={FiBookOpen} className="w-6 h-6 text-brand-primary" />
            </div>
            <div>
               <h1 className="text-xl font-bold text-gray-900 tracking-tight">
                Handlungsempfehlungen
              </h1>
              <p className="text-xs text-gray-500">
                Wissensdatenbank für Team- und Organisationsentwicklung
              </p>
            </div>
          </div>
          
           <div className="flex items-center gap-4">
             {/* Content Level Toggle */}
             <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
               <button
                onClick={() => setContentLevel('short')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  contentLevel === 'short' 
                    ? 'bg-white text-brand-primary shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Kurzübersicht anzeigen"
              >
                <SafeIcon icon={FiGrid} className="w-4 h-4" />
                Kurz
              </button>
              <button
                onClick={() => setContentLevel('detailed')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  contentLevel === 'detailed' 
                    ? 'bg-white text-brand-primary shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title="Vollständige Details anzeigen"
              >
                <SafeIcon icon={FiAlignLeft} className="w-4 h-4" />
                Vollständig
              </button>
             </div>

             <div className="w-px h-8 bg-gray-200"></div>

             {/* View Mode Toggle */}
             <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
               <button
                onClick={() => setViewMode('visual')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'visual' 
                    ? 'bg-white text-brand-primary shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <SafeIcon icon={FiLayout} className="w-4 h-4" />
                Visuell
              </button>
              <button
                onClick={() => setViewMode('markdown')}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'markdown' 
                    ? 'bg-white text-brand-primary shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <SafeIcon icon={FiFileText} className="w-4 h-4" />
                Text / LLM
              </button>
              <div className="w-px h-4 bg-gray-300 mx-1"></div>
              <button
                onClick={handleCopy}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium text-gray-700 hover:bg-white hover:text-brand-primary transition-all"
                title="Markdown für KI kopieren"
              >
                <SafeIcon icon={FiCopy} className="w-4 h-4" />
                {copyFeedback ? 'Kopiert!' : 'Kopieren'}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[80%] w-full mx-auto px-4 py-8 sm:px-6 lg:px-8">
        
        {viewMode === 'visual' ? (
          // VISUAL MODE
          <div className="space-y-8">
            <div className="prose prose-lg max-w-none text-gray-600">
              <p>
                {contentLevel === 'short' 
                  ? "Verschaffen Sie sich einen schnellen Überblick über alle verfügbaren Handlungsempfehlungen." 
                  : "Detaillierte Ansicht aller Handlungsempfehlungen mit vollständigen Beschreibungen und Anleitungen."}
              </p>
            </div>

            {recommendations.length > 0 ? (
              contentLevel === 'short' ? (
                // SHORT OVERVIEW (Grid)
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {recommendations.map(rec => (
                    <div key={`short-${rec.id}`} className="bg-gray-50 rounded-xl p-6 border border-gray-200 hover:border-brand-primary/30 transition-colors h-full flex flex-col">
                      <h3 className="font-bold text-gray-900 mb-3 leading-tight">{rec.title}</h3>
                      {rec.short_description ? (
                         <p className="text-sm text-gray-600 leading-relaxed flex-grow">{rec.short_description}</p>
                      ) : (
                         <p className="text-xs text-gray-400 italic flex-grow">Keine Kurzbeschreibung verfügbar</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                // DETAILED VIEW (List)
                <div className="space-y-10">
                  {recommendations.map((rec) => (
                    <article 
                      key={rec.id} 
                      className="group bg-white rounded-2xl border border-gray-200 p-8 shadow-sm hover:shadow-md transition-all duration-300"
                      itemScope 
                      itemType="https://schema.org/Article"
                    >
                      <header className="mb-6">
                        <h2 
                          className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-brand-primary transition-colors"
                          itemProp="headline"
                        >
                          {rec.title}
                        </h2>
                        {rec.short_description && (
                          <p className="text-lg text-gray-600 leading-relaxed" itemProp="description">
                            {rec.short_description}
                          </p>
                        )}
                      </header>
                      
                      <div className="prose prose-blue max-w-none border-t border-gray-100 pt-6">
                        <div 
                          dangerouslySetInnerHTML={{ __html: rec.description }} 
                          itemProp="articleBody"
                          className="recommendation-content text-gray-700 leading-relaxed"
                        />
                      </div>
                    </article>
                  ))}
                </div>
              )
            ) : (
              <div className="text-center py-12">
                <SafeIcon icon={FiLayers} className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900">Keine öffentlichen Empfehlungen</h3>
                <p className="text-gray-500 mt-2">Derzeit sind keine Handlungsempfehlungen veröffentlicht.</p>
              </div>
            )}
          </div>
        ) : (
          // MARKDOWN MODE for LLMs
          <div className="bg-gray-900 rounded-xl overflow-hidden shadow-2xl border border-gray-700">
            <div className="bg-gray-800 px-4 py-2 border-b border-gray-700 flex justify-between items-center">
              <span className="text-gray-400 text-xs font-mono">raw_content.md ({contentLevel === 'short' ? 'Kurz' : 'Vollständig'})</span>
              <button 
                onClick={handleCopy}
                className="text-xs text-brand-primary hover:text-white transition-colors"
              >
                {copyFeedback ? 'Kopiert!' : 'Alles kopieren'}
              </button>
            </div>
            <pre className="p-6 text-gray-300 font-mono text-sm overflow-x-auto whitespace-pre-wrap leading-relaxed">
              {generateMarkdown()}
            </pre>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200 mt-12">
        <div className="max-w-[80%] w-full mx-auto px-4 py-8 sm:px-6 lg:px-8 text-center text-gray-500 text-sm">
          <p>&copy; {new Date().getFullYear()} Organisation Development Platform. Alle Rechte vorbehalten.</p>
        </div>
      </footer>
    </div>
  );
};

export default PublicRecommendations;